import React from 'react';
import { Image } from 'lucide-react';

interface PageHeaderConfig {
    title?: string;
    description?: string;
    logoUrl?: string;
    showLogo?: boolean;
    logoPlacement?: 'left' | 'right';
    style?: {
        title?: any;
        description?: any;
        plotArea?: any;
        design?: any;
    };
}

export const PageHeaderWidget = ({ config = {} }: { config?: PageHeaderConfig }) => {
    // Defaults
    const title = config.title || "Dashboard Title";
    const description = config.description || "";
    const logoUrl = config.logoUrl || "";
    const showLogo = config.showLogo !== false; // Default true
    const logoPlacement = config.logoPlacement || 'left'; // 'left' | 'right'
    const titleStyle = config.style?.title || {};
    const descStyle = config.style?.description || {};

    const plotStyle = config.style?.plotArea || {};
    const designStyle = config.style?.design || {};

    return (
        <div
            className="w-full h-full flex items-center overflow-hidden transition-all"
            style={{
                backgroundColor: designStyle.cardBackgroundColor || 'transparent',
                borderRadius: designStyle.borderRadius ? `${designStyle.borderRadius}px` : undefined,
                boxShadow: designStyle.shadow !== 'none' ? designStyle.shadow : undefined,
                paddingTop: plotStyle.paddingTop !== undefined ? `${plotStyle.paddingTop}px` : '0px',
                paddingBottom: plotStyle.paddingBottom !== undefined ? `${plotStyle.paddingBottom}px` : '0px',
                paddingLeft: plotStyle.paddingLeft !== undefined ? `${plotStyle.paddingLeft}px` : '16px', // Default px-4 = 16px
                paddingRight: plotStyle.paddingRight !== undefined ? `${plotStyle.paddingRight}px` : '16px',
                justifyContent: 'space-between'
            }}
        >
            {/* Left Content Group (Logo [if left] + Text) */}
            <div className={`flex items-center gap-4 flex-1 min-w-0 ${logoPlacement === 'right' ? 'flex-row' : 'flex-row'}`}>

                {/* Logo (Left) */}
                {showLogo && logoPlacement !== 'right' && (
                    <div className="shrink-0">
                        {logoUrl ? (
                            <img src={logoUrl} alt="Logo" className="h-10 w-auto object-contain rounded-sm select-none" draggable={false} />
                        ) : (
                            <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center text-slate-400">
                                <Image className="w-5 h-5" />
                            </div>
                        )}
                    </div>
                )}

                {/* Text Block */}
                <div className="flex-1 min-w-0 flex flex-col justify-center py-2">
                    <h1
                        className="truncate"
                        style={{
                            color: titleStyle.color || '#1e293b',
                            fontSize: titleStyle.fontSize ? `${titleStyle.fontSize}px` : '24px',
                            fontWeight: titleStyle.bold ? 'bold' : 'normal',
                            fontStyle: titleStyle.italic ? 'italic' : 'normal',
                            textDecoration: titleStyle.underline ? 'underline' : 'none',
                            textAlign: titleStyle.align || 'left',
                            marginBottom: '4px',
                            lineHeight: '1.5',
                            paddingBottom: '2px'
                        }}
                    >
                        {title}
                    </h1>
                    {description && (
                        <p
                            className="truncate opacity-90"
                            style={{
                                color: descStyle.color || '#64748b',
                                fontSize: descStyle.fontSize ? `${descStyle.fontSize}px` : '14px',
                                fontWeight: descStyle.bold ? 'bold' : 'normal',
                                fontStyle: descStyle.italic ? 'italic' : 'normal',
                                textDecoration: descStyle.underline ? 'underline' : 'none',
                                textAlign: descStyle.align || 'left',
                                lineHeight: '1.5',
                                paddingBottom: '2px'
                            }}
                        >
                            {description}
                        </p>
                    )}
                </div>
            </div>

            {/* Logo (Right) */}
            {showLogo && logoPlacement === 'right' && (
                <div className="shrink-0 ml-4">
                    {logoUrl ? (
                        <img src={logoUrl} alt="Logo" className="h-10 w-auto object-contain rounded-sm select-none" draggable={false} />
                    ) : (
                        <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center text-slate-400">
                            <Image className="w-5 h-5" />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
